import { Checkbox } from "@mui/material"
import type { ColumnDef, RowSelectionState } from "@tanstack/react-table"
import type { FC } from "react"

import { ProductIdCell } from "./ProductIdCell"
import { SupplierNameCell } from "./SupplierNameCell"

import { LongWordWrapper } from "@/components/table/LongWordWrapper"
import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { Product } from "@/graphql/codegen/graphql"

interface AkrochemProductsTableProps {
  products: Product[]
  loading: boolean
  selected: Record<string, boolean>
  onSelectionChange: (selected: Record<string, boolean>) => void
  associatedIds?: number[]
}

export const AkrochemProductsTable: FC<AkrochemProductsTableProps> = ({
  products,
  loading,
  selected,
  onSelectionChange,
  associatedIds = [],
}) => {
  const handleRowSelectionChange = (
    updaterOrValue: ((old: RowSelectionState) => RowSelectionState) | RowSelectionState
  ) => {
    const newSelection = typeof updaterOrValue === "function" ? updaterOrValue(selected) : updaterOrValue

    onSelectionChange(newSelection)
  }

  const columns: ColumnDef<Product>[] = [
    {
      id: "select",
      header: "",
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      size: 50,
    },
    {
      header: "Product ID",
      cell: ({ row }) => (
        <ProductIdCell
          externalSystemId={row.original?.externalSystemId}
          publicId={row.original?.publicId}
          computedProductStatus={row.original?.computedProductStatus}
        />
      ),
      enableSorting: false,
    },
    {
      header: "Supplier Name",
      enableSorting: false,
      cell: ({ row }) => (
        <SupplierNameCell
          companyName={row.original?.company?.name}
          externalSystemId={row.original?.company?.externalSystemId}
        />
      ),
    },
    {
      accessorKey: "companyProductDescription",
      header: "Akrochem Desc",
      enableSorting: false,
      size: 128,
      cell: ({ row }) => <LongWordWrapper>{row.original.companyProductDescription}</LongWordWrapper>,
    },
    {
      accessorKey: "productFormInfo",
      header: "Form",
      enableSorting: false,
      cell: ({ row }) => row.original.productFormInfo || "",
      minSize: 100,
    },
  ]

  return (
    <AerosBaseTable
      features={{
        tanstackOptions: {
          data: products,
          columns,
          state: {
            rowSelection: selected,
            isLoading: loading,
          },
          onRowSelectionChange: handleRowSelectionChange,
          getRowId: (row) => row.productId.toString(),
          enableRowSelection: (row) => !associatedIds.includes(row.original.productId),
        },
      }}
      slotProps={{
        bodyRow: {
          className: "cursor-pointer hover:bg-primary-100",
        },
      }}
      noDataMessage='No products found'
    />
  )
}
